import { DatesSetArg } from "@fullcalendar/react";
import { isSameDay } from "date-fns";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { accountApi } from "@/apis/accounts/accountApi";
import { CalendarServiceType } from "@/apis/clients";
import { ApiResponseError } from "@/apis/errors";
import { Button } from "@/components/atoms/buttons/Button";
import { Select } from "@/components/atoms/inputForms/Select";
import { SectionTitle } from "@/components/atoms/typograhies/SectionTitle";
import CalendarDateModal from "@/components/organisms/mypage/CalendarDateModal";
import { Calendar } from "@/foundations/fullCalendar/Calendar";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { getCookie, setCookie } from "@/libs/service/cookie";
import { useLoading } from "@/states/loading/operations";
import { assertIsDefined } from "@/utils/common";
import { NEXT_URL } from "@/utils/const";
import { format } from "@/utils/date";

export interface CalendarEvent {
  id: number;
  date: Date;
  name: string;
  start: Date;
  end: Date;
  type: CalendarServiceType;
  service_id: number;
  customer_name?: string;
}

interface ModalState {
  date: Date;
  events: CalendarEvent[];
}

interface ServiceTypeState {
  type?: CalendarServiceType;
  start?: Date;
  end?: Date;
}

export interface CalendarRange {
  start: Date;
  end: Date;
}

interface Props {
  getEvents: (
    range: CalendarRange,
    type?: CalendarServiceType,
  ) => Promise<CalendarEvent[]>;
}

export default function MyPageCalendar({ getEvents }: Props) {
  const router = useRouter();
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();

  // FullCalendarの警告が出るためSSRしない
  const [isClient, setIsClient] = useState(false);

  const [events, setEvents] = useState<CalendarEvent[]>([]);

  const [modalState, setModalState] = useState<ModalState | undefined>(
    undefined,
  );

  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    type: undefined,
    start: undefined,
    end: undefined,
  });

  const [isSyncGoogle, setIsSyncGoogle] = useState<boolean>(false);

  const isModalOpen = !!modalState;
  const canChooseType = false;

  const fetchEvents = (start: Date, end: Date, type?: CalendarServiceType) => {
    void withLoading(async () => {
      try {
        const es = await getEvents({ start, end }, type);
        setEvents(es);
        type && setCookie(`calendar_service_type=${type}`);
      } catch (e) {
        if (e instanceof ApiResponseError) {
          await handlePageError(e);
        }
      }
    });
  };

  const handleDatesSet = (arg: DatesSetArg) => {
    const { start, end } = arg;
    setServiceType((prev) => {
      return {
        ...prev,
        start: start,
        end: end,
      };
    });
    fetchEvents(start, end, serviceType.type);
  };

  const handleChangeType = (type: CalendarServiceType) => {
    setServiceType((prev) => {
      return {
        ...prev,
        type,
      };
    });
  };

  useEffect(() => {
    const calender_service_type = getCookie("calendar_service_type");
    if (calender_service_type !== "" && canChooseType) {
      if (Number(calender_service_type) === CalendarServiceType.CALENDAR) {
        handleChangeType(CalendarServiceType.CALENDAR);
      } else if (Number(calender_service_type) === CalendarServiceType.EVENT) {
        handleChangeType(CalendarServiceType.EVENT);
      }
    }
    setIsClient(true);
  }, []);

  useEffect(() => {
    void withLoading(async () => {
      const res = await accountApi.getCalendarSetting();
      if (res && res.data && res.data.gcalendar_email) {
        setIsSyncGoogle(true);
      }
    });
  }, []);

  if (!isClient) {
    return <></>;
  }

  const handleDateClick = (date: Date) => {
    const eventsOfDate: CalendarEvent[] = events.filter((e) =>
      isSameDay(e.date, date),
    );
    if (eventsOfDate.length === 0) {
      return;
    }
    if (eventsOfDate.length === 1) {
      const event = eventsOfDate[0];
      if (event.type === CalendarServiceType.CALENDAR) {
        void router.push({
          pathname: NEXT_URL.RESERVATIONS_DETAIL,
          query: { id: event.id },
        });
      } else {
        void router.push({
          pathname: NEXT_URL.SERVICES_DETAIL,
          query: { id: event.service_id },
        });
      }
      return;
    }
    eventsOfDate.sort((a, b) =>
      new Date(a.start) >= new Date(b.start) ? 1 : -1,
    );
    setModalState({
      date,
      events: eventsOfDate,
    });
  };

  const handleSubmit = () => {
    if (serviceType.start && serviceType.end) {
      fetchEvents(serviceType.start, serviceType.end, serviceType.type);
    }
  };

  const getColor = (type: CalendarServiceType | string) => {
    switch (type) {
      case CalendarServiceType.EVENT:
        return { backgroundColor: "#E1F7F4", borderColor: "#45BEAB" };
      case CalendarServiceType.CALENDAR:
        return { backgroundColor: "#D7EEFF", borderColor: "#0053AD" };
      default:
        return { backgroundColor: "#E1F7F4", borderColor: "#45BEAB" };
    }
  };

  const Legend = ({
    type,
    className,
  }: {
    type: CalendarServiceType | string;
    className?: string;
  }) => {
    const defaultClassname = [
      "min-w-[150px] text-sm font-normal leading-tight rounded-[2px] border-l-[3px] py-1 pl-[5px] pr-2.5",
      className,
    ].join(" ");
    switch (type) {
      case CalendarServiceType.EVENT:
        return (
          <div className={`${defaultClassname}`} style={getColor(type)}>
            イベント/セミナー予定
          </div>
        );
      case CalendarServiceType.CALENDAR:
        return (
          <div className={`${defaultClassname}`} style={getColor(type)}>
            カレンダー予定
          </div>
        );
      case "Google":
        return (
          <div className={`${defaultClassname}`} style={getColor(type)}>
            Googleカレンダー
          </div>
        );
      default:
        return (
          <div className={`${defaultClassname}`} style={getColor(type)}>
            イベント/セミナー予定
          </div>
        );
    }
  };

  return (
    <div className="mypage-calendar px-4 md:px-0 space-y-4">
      <div className="flex flex-col md:flex-row items-start md:justify-between md:gap-4 md:mb-[30px] md:items-center">
        <SectionTitle title="直近の予定" className="mb-2 md:m-0" />
        <div className="flex flex-wrap items-center gap-2">
          <Legend type={CalendarServiceType.EVENT} />
          <Legend type={CalendarServiceType.CALENDAR} />
          <Legend type={"Google"} className="w-[164px] opacity-0 invisible" />
        </div>
      </div>

      {canChooseType && (
        <div className="flex items-center px-4 mb-2 md:justify-end md:px-0 md:mr-1 md:mb-4">
          <Select
            onChange={(selected) => {
              switch (Number(selected)) {
                case CalendarServiceType.CALENDAR:
                  handleChangeType(CalendarServiceType.CALENDAR);
                  return;
                case CalendarServiceType.EVENT:
                  handleChangeType(CalendarServiceType.EVENT);
                  return;
                default:
                  throw new Error("カレンダーアクションタイプが不正です");
              }
            }}
            options={[
              { text: "カレンダー", value: CalendarServiceType.CALENDAR },
              { text: "イベント", value: CalendarServiceType.EVENT },
            ]}
            value={serviceType.type}
            className="mr-2 w-[calc(100%-72px)] md:w-[200px]"
          />
          <Button
            type="button"
            data-testid="login-button"
            label="切替"
            size="sm"
            onClick={handleSubmit}
            className="!h-11"
          />
        </div>
      )}
      <Calendar
        events={events.map((e) => ({
          id: `${e.id}`,
          start: `${format(new Date(e.start), "yyyy-MM-dd HH:mm")}`,
          date: e.date,
          end: `${format(new Date(e.end), "yyyy-MM-dd HH:mm")}`,
          title: e.customer_name || e.name,
          backgroundColor: getColor(e.type).backgroundColor,
          borderColor: getColor(e.type).borderColor,
        }))}
        handleDateClick={(arg) => handleDateClick(arg.date)}
        eventClick={(arg) => {
          assertIsDefined(arg.event.start);
          handleDateClick(arg.event.start);
        }}
        moreLinkClick={(arg) => {
          handleDateClick(arg.date);
          // 無意味な文字列をreturnするとデフォルトの挙動（popover）を無効化できる模様
          return "false";
        }}
        datesSet={handleDatesSet}
        isSyncGoogle={isSyncGoogle}
      />

      {modalState && (
        <CalendarDateModal
          isOpen={isModalOpen}
          date={modalState.date}
          events={modalState.events}
          handleClose={() => setModalState(undefined)}
        />
      )}
    </div>
  );
}
